import gql from 'graphql-tag';
import { Query, Mutation } from 'core';
import { GROUP_FIELDS } from 'apps/group/gql';
import { DASHBOARD_PROJECT_CONNECTION_FIELDS } from 'apps/project/gql';

export const SHARED_FILE_REVISION = gql`
  fragment sharedFileRevision on SharedFileRevision {
    id
    version
    dateCreated
    lastUpdated
    title
    creator {
      id
      username
    }
    file {
      id
      filename
      mimeType
      path
      size
      creator {
        id
        username
      }
      completed
      fileLastModified
      permalink
      canDownload
      url
      private
      originalFile {
        id
      }
      space {
        id
        parent {
          id
        }
      }
      conversionGltfjson(conversionSpec: $conversionSpec) {
        id
        status
        reason
        errorCode
        convertedFile {
          id
        }
      }
    }
  }
`;

const SHARED_FILE_SINGLE = gql`
  ${SHARED_FILE_REVISION}
  ${GROUP_FIELDS}
  fragment sharedFile on SharedFile {
    id
    canUpdate
    canDelete
    isPrivate
    creator {
      id
      username
    }
    slug
    name
    revision {
      ...sharedFileRevision
    }
    context {
      id
      parentObj {
        id
        canUpdate
      }
    }
    groups {
      ...groupFields
    }
    space {
      id
      spaceType
      trialPeriodEnd
      hasProSubscription
      remainingSharedFiles
      content {
        id
        slug
      }
    }
    inSpace {
      id
      whichTypes
      spaceType
      parent {
        id
        spaceType
        content {
          id
          slug
        }
      }
      content {
        id
        slug
      }
    }
  }
`;

const CREATE_SHARED_FILE_MUTATION = gql`
  ${SHARED_FILE_SINGLE}
  mutation CreateSharedFile(
    $createSharedFileInput: CreateSharedFileInput!
    $conversionSpec: String
  ) {
    createSharedFile(createSharedFileInput: $createSharedFileInput) {
      sharedFile {
        ...sharedFile
      }
    }
  }
`;

export class CreateSharedFileMutation extends Mutation {
  static conf = {
    GQL: CREATE_SHARED_FILE_MUTATION,
    inputName: 'createSharedFileInput',
    resultPath: 'createSharedFile.sharedFile',
  };
}

const UPDATE_SHARED_FILE_MUTATION = gql`
  ${SHARED_FILE_SINGLE}
  mutation UpdateSharedFile(
    $updateSharedFileInput: UpdateSharedFileInput!
    $conversionSpec: String
  ) {
    updateSharedFile(updateSharedFileInput: $updateSharedFileInput) {
      sharedFile {
        ...sharedFile
      }
    }
  }
`;

export class UpdateSharedFileMutation extends Mutation {
  static conf = {
    GQL: UPDATE_SHARED_FILE_MUTATION,
    inputName: 'updateSharedFileInput',
    resultPath: 'updateSharedFile.sharedFile',
  };
}

const SHARED_FILE_QUERY = gql`
  ${SHARED_FILE_SINGLE}
  query SharedFile($id: ID!, $version: String, $conversionSpec: String) {
    sharedFile(id: $id, version: $version) {
      ...sharedFile
      isRestricted
    }
  }
`;

export class SharedFileQuery extends Query {
  static conf = {
    GQL: SHARED_FILE_QUERY,
    resultPath: 'sharedFile',

    varNames: ['id', 'version', 'conversionSpec'],
  };
}

const SHARED_FILE_REVISIONS_QUERY = gql`
  ${SHARED_FILE_REVISION}
  query SharedFileRevisions(
    $id: ID!
    $batchSize: Int
    $cursor: String
    $conversionSpec: String
  ) {
    sharedFile(id: $id) {
      revisions(first: $batchSize, after: $cursor) {
        pageInfo {
          startCursor
          endCursor
          hasNextPage
        }
        totalCount
        edges {
          node {
            ...sharedFileRevision
          }
        }
      }
    }
  }
`;

export class SharedFileRevisionsQuery extends Query {
  static conf = {
    GQL: SHARED_FILE_REVISIONS_QUERY,
    resultPath: 'sharedFile.revisions',

    varNames: ['id', 'batchSize', 'cursor', 'conversionSpec'],
  };
}

const SHARED_FILE_CONNECTION = gql`
  fragment sharedFileConnection on SharedFile {
    id
    name
    canDelete
    revisions {
      totalCount
    }
    revision {
      id
      lastUpdated
      file {
        id
        filename
        conversionGltfjson {
          id
          status
        }
      }
    }
    space {
      id
      spaceType
      content {
        id
        title
        slug
      }
    }
    creator {
      id
      profile {
        id
        inSpace {
          id
          spaceType
          content {
            id
            slug
          }
        }
      }
    }
  }
`;

const PROFILE_SHARED_FILES_QUERY = gql`
  ${SHARED_FILE_CONNECTION}
  query ProfileSharedFiles(
    $username: String!
    $batchSize: Int
    $cursor: String
  ) {
    profile(username: $username) {
      id
      sharedFiles(sortBy: "recent", first: $batchSize, after: $cursor) {
        pageInfo {
          startCursor
          endCursor
          hasNextPage
        }
        totalCount
        edges {
          node {
            ...sharedFileConnection
          }
        }
      }
    }
  }
`;

const PROFILE_SHARED_WITH_FILES_QUERY = gql`
  ${SHARED_FILE_CONNECTION}
  query profileSharedWithFiles(
    $username: String!
    $batchSize: Int
    $cursor: String
  ) {
    profile(username: $username) {
      id
      sharedWithFiles(sortBy: "recent", first: $batchSize, after: $cursor) {
        pageInfo {
          startCursor
          endCursor
          hasNextPage
        }
        totalCount
        edges {
          cursor
          node {
            ...sharedFileConnection
          }
        }
      }
    }
  }
`;

export class ProfileSharedWithFilesQuery extends Query {
  static conf = {
    GQL: PROFILE_SHARED_WITH_FILES_QUERY,
    resultPath: 'profile.sharedWithFiles',
    varNames: ['username', 'batchSize', 'cursor'],
  };
}

const PROFILE_SHARED_WITH_PROJECTS_QUERY = gql`
  ${DASHBOARD_PROJECT_CONNECTION_FIELDS}
  query profileSharedWithProjects(
    $username: String!
    $cursor: String
    $batchSize: Int
    $sortBy: String
  ) {
    profile(username: $username) {
      id
      sharedWithProjects(first: $batchSize, after: $cursor, sortBy: $sortBy) {
        ...dashboardProjectConnectionFields
      }
    }
  }
`;
export class ProfileSharedWithProjectsQuery extends Query {
  static conf = {
    GQL: PROFILE_SHARED_WITH_PROJECTS_QUERY,
    varNames: ['username', 'cursor', 'sortBy', 'batchSize'],
    resultPath: 'profile.sharedWithProjects',
  };
}

export class ProfileSharedFilesQuery extends Query {
  static conf = {
    GQL: PROFILE_SHARED_FILES_QUERY,
    resultPath: 'profile.sharedFiles',

    varNames: ['username', 'batchSize', 'cursor'],
  };
}

const INITIATIVE_SHARED_FILES_QUERY = gql`
  ${SHARED_FILE_CONNECTION}
  query InitiativeSharedFiles(
    $slug: String!
    $batchSize: Int
    $cursor: String
  ) {
    initiative(slug: $slug) {
      id
      sharedFiles(sortBy: "recent", first: $batchSize, after: $cursor) {
        pageInfo {
          startCursor
          endCursor
          hasNextPage
        }
        totalCount
        edges {
          cursor
          node {
            ...sharedFileConnection
          }
        }
      }
    }
  }
`;

export class InitiativeSharedFilesQuery extends Query {
  static conf = {
    GQL: INITIATIVE_SHARED_FILES_QUERY,
    resultPath: 'initiative.sharedFiles',

    varNames: ['slug', 'batchSize', 'cursor'],
  };
}

const SHARED_FILE_COLLABORATORS_QUERY = gql`
  ${GROUP_FIELDS}
  query SharedFileCollaborators($id: ID!) {
    sharedFile(id: $id) {
      id
      groups {
        ...groupFields
      }
    }
  }
`;

export class SharedFileCollaboratorsQuery extends Query {
  static conf = {
    GQL: SHARED_FILE_COLLABORATORS_QUERY,
    resultPath: 'sharedFile',

    varNames: ['id'],
  };
}

const DELETE_SHARED_FILE_MUTATION = gql`
  mutation DeleteSharedFile($deleteSharedFileInput: DeleteSharedFileInput!) {
    deleteSharedFile(deleteSharedFileInput: $deleteSharedFileInput) {
      result
    }
  }
`;

export class DeleteSharedFileMutation extends Mutation {
  static conf = {
    GQL: DELETE_SHARED_FILE_MUTATION,
    inputName: 'deleteSharedFileInput',
    resultPath: 'deleteSharedFile',
  };
}
